import React, { useEffect, useState } from 'react';
import DateFilter from './DateFilter';
import Spin from 'src/components/common/Spin';
import ErrorAlert from 'src/components/common/ErrorAlert';
import PriceChart from './PriceChart';
import CompetitorFilter from './CompetitorFilter';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import usePriceHistoryListFetch from 'src/components/ProductDetails/hooks/usePriceHistoryListFetch';
import { getCompetitors, filterChartData } from 'src/components/ProductDetails/services/PriceHistory.service';
import PriceChartItem from 'src/components/ProductDetails/types/PriceChartItem';
import DateRange from 'src/components/ProductDetails/types/DateRange';
import styles from './PriceHistory.module.less';

type PriceHistoryProps = {
  ownerName: string;
  sourceOwnerId: string;
  sourceProductId: string;
};

const PriceHistory: React.FC<PriceHistoryProps> = ({ ownerName, sourceOwnerId, sourceProductId }) => {
  const { matchTypeFilter, priceType, region } = useQueryUrlParams();
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const { startDate = '', endDate = '' } = dateRange ?? {};
  const [{ loading, data, error }] = usePriceHistoryListFetch({
    regionId: region,
    sourceOwnerId,
    sourceProductId,
    matchType: matchTypeFilter,
    priceType,
  });
  const [competitorList, setCompetitorList] = useState<string[]>([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([]);
  const [filteredData, setFilteredData] = useState<PriceChartItem[]>([]);
  const noResultText =
    selectedCompetitors.length > 0 && startDate && endDate
      ? 'No results found'
      : 'Select Competitors and Date range to see results';

  useEffect(() => {
    setFilteredData(filterChartData(data, startDate, endDate, selectedCompetitors));
    setCompetitorList(getCompetitors(data));
  }, [data?.length]);

  useEffect(() => {
    setFilteredData(filterChartData(data, startDate, endDate, selectedCompetitors));
  }, [selectedCompetitors, startDate, endDate]);

  return (
    <div className={styles.priceHistoryWrapper}>
      <div>
        <CompetitorFilter competitors={competitorList} onUpdate={setSelectedCompetitors} />
        <DateFilter onUpdate={setDateRange} />
      </div>
      <ErrorAlert isVisible={error} errorLabel={'Price History'} />
      <div className={styles.chartContainer}>
        <Spin spinning={loading} size="large">
          {filteredData.length > 0 ? (
            <PriceChart ownerName={ownerName} data={filteredData} selectedCompetitors={selectedCompetitors} />
          ) : (
            <p className={styles.noResults}>{noResultText}</p>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default PriceHistory;
